import React from 'react';
import {
  User, CreditCard, Shield, Truck, BookOpen,
  CheckCircle, MapPin, AlertCircle, Award, Building2,
} from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { SMKMeter } from './SMKMeter';
import { VocationalLicenseCardMY } from './VocationalLicenseCardMY';

const TAB_DEFS = (language) => [
  { id: 'overview', label: language === 'bm' ? 'Maklumat Peribadi' : 'Personal Info', icon: User },
  { id: 'classes', label: language === 'bm' ? 'Kelas Lesen' : 'License Classes', icon: CreditCard },
  { id: 'smk', label: 'SMK', icon: Shield },
  { id: 'vocational', label: language === 'bm' ? 'Lesen Vokasional' : 'Vocational', icon: Truck },
  { id: 'exam', label: language === 'bm' ? 'Ujian & Latihan' : 'Tests & Training', icon: BookOpen },
];

const OverviewTab = ({ result, language }) => {
  const fields = [
    { key: 'name', label: language === 'bm' ? 'Nama Penuh' : 'Full Name', value: result.name },
    { key: 'ic', label: language === 'bm' ? 'No. Kad Pengenalan' : 'IC Number', value: result.ic, mono: true },
    { key: 'dob', label: language === 'bm' ? 'Tarikh Lahir' : 'Date of Birth', value: result.dob },
    { key: 'gender', label: language === 'bm' ? 'Jantina' : 'Gender', value: result.gender },
    { key: 'nationality', label: language === 'bm' ? 'Kewarganegaraan' : 'Nationality', value: result.nationality },
    { key: 'jenisPemegang', label: language === 'bm' ? 'Jenis Pemegang' : 'Holder Type', value: result.jenisPemegang },
    { key: 'licenseNo', label: language === 'bm' ? 'No. Lesen Memandu' : 'License Number', value: result.licenseNo, mono: true },
    { key: 'issueDate', label: language === 'bm' ? 'Tarikh Dikeluarkan' : 'Issue Date', value: result.issueDate },
    { key: 'expiryDate', label: language === 'bm' ? 'Tarikh Luput' : 'Expiry Date', value: result.expiryDate },
    { key: 'status', label: language === 'bm' ? 'Status Lesen' : 'License Status', value: result.status, isStatus: true },
    { key: 'negeriLesen', label: language === 'bm' ? 'Negeri Lesen' : 'License State', value: result.negeriLesen },
    { key: 'cawanganJPJ', label: language === 'bm' ? 'Cawangan JPJ' : 'JPJ Branch', value: result.cawanganJPJ },
    { key: 'address', label: language === 'bm' ? 'Alamat Berdaftar' : 'Registered Address', value: result.address, full: true },
    { key: 'syaratKhas', label: language === 'bm' ? 'Syarat / Sekatan Khas' : 'Special Conditions', value: result.syaratKhas, full: true },
  ];

  return (
    <div className="p-5">
      {result.sekatan && result.sekatan.length > 0 && (
        <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3 flex gap-2">
          <AlertCircle size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-orange-800 mb-1">
              {language === 'bm' ? 'Sekatan Lesen:' : 'License Restrictions:'}
            </p>
            {result.sekatan.map((s) => (
              <p key={s} className="text-xs text-orange-700 font-semibold">{s}</p>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((field) => (
          <div key={field.key} className={`${field.full ? 'md:col-span-2' : ''} border-b border-gray-50 pb-3`}>
            <div className="text-xs text-gray-400 mb-0.5">{field.label}</div>
            {field.isStatus ? (
              <StatusBadge status={field.value} />
            ) : (
              <div className={`text-sm font-semibold text-gray-800 ${field.mono ? 'font-mono tracking-wider' : ''}`}>
                {field.value}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ClassesTab = ({ result, language }) => (
  <div className="p-5">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: '#003580' }}>
            <th className="text-left px-4 py-3 text-white text-xs font-semibold">{language === 'bm' ? 'Kelas' : 'Class'}</th>
            <th className="text-left px-4 py-3 text-white text-xs font-semibold">{language === 'bm' ? 'Penerangan' : 'Description'}</th>
            <th className="text-left px-4 py-3 text-white text-xs font-semibold">{language === 'bm' ? 'Tarikh Dikeluarkan' : 'Issue Date'}</th>
            <th className="text-left px-4 py-3 text-white text-xs font-semibold">{language === 'bm' ? 'Endorsemen' : 'Endorsement'}</th>
            <th className="text-center px-4 py-3 text-white text-xs font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {result.classes.map((cls, i) => (
            <tr key={`${cls.class}-${cls.issueDate}`} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="px-4 py-3">
                <span className="font-black text-sm px-2 py-1 rounded" style={{ backgroundColor: '#003580', color: 'white' }}>
                  {cls.class}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-gray-600">{cls.description}</td>
              <td className="px-4 py-3 text-xs text-gray-600 font-mono">{cls.issueDate}</td>
              <td className="px-4 py-3 text-xs text-gray-600">
                {cls.endorsement && cls.endorsement !== '-'
                  ? <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-bold">{cls.endorsement}</span>
                  : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-3 text-center">
                <StatusBadge status={cls.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
      <p className="text-xs text-blue-700">
        <span className="font-bold">{language === 'bm' ? 'Nota:' : 'Note:'}</span>
        {language === 'bm'
          ? ' Kelas lesen memandu menentukan jenis kenderaan yang boleh anda pandu. Hanya kelas yang berstatus AKTIF yang sah digunakan. Endorsemen "P" bermaksud Lesen Percubaan (Probationary).'
          : ' License classes determine the type of vehicle you are allowed to drive. Only classes with ACTIVE status are valid. Endorsement "P" means Probationary License.'}
      </p>
    </div>
  </div>
);

const SmkTab = ({ result, language }) => (
  <div className="p-5">
    <div className="mb-6">
      <h3 className="font-bold text-gray-800 text-sm mb-4">
        {language === 'bm' ? 'Mata Kesalahan Terkumpul' : 'Accumulated Demerit Points'}
      </h3>
      <SMKMeter points={result.smkPoints} />
    </div>
    {result.violations.length > 0 ? (
      <div>
        <h3 className="font-bold text-gray-800 text-sm mb-3">
          {language === 'bm' ? 'Rekod Kesalahan' : 'Violation Records'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: '#cc0000' }}>
                <th className="text-left px-3 py-2.5 text-white text-xs">{language === 'bm' ? 'Tarikh' : 'Date'}</th>
                <th className="text-left px-3 py-2.5 text-white text-xs">{language === 'bm' ? 'Kesalahan' : 'Offense'}</th>
                <th className="text-left px-3 py-2.5 text-white text-xs">{language === 'bm' ? 'Mahkamah' : 'Court'}</th>
                <th className="text-center px-3 py-2.5 text-white text-xs">{language === 'bm' ? 'Mata' : 'Points'}</th>
                <th className="text-center px-3 py-2.5 text-white text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              {result.violations.map((v, i) => (
                <tr key={`${v.date}-${v.offense}`} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-3 py-2.5 text-xs font-mono text-gray-600 whitespace-nowrap">{v.date}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-700">{v.offense}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{v.court}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="font-bold text-red-600">+{v.points}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">{v.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : (
      <div className="flex flex-col items-center py-8 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
          <CheckCircle size={28} className="text-green-500" />
        </div>
        <h4 className="font-bold text-gray-700">{language === 'bm' ? 'Rekod Bersih!' : 'Clean Record!'}</h4>
        <p className="text-xs text-gray-400 mt-1">{language === 'bm' ? 'Tiada rekod kesalahan trafik' : 'No traffic violation records'}</p>
      </div>
    )}
    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
      <p className="text-xs text-yellow-800">
        <span className="font-bold">SMK:</span>
        {language === 'bm'
          ? ' Sistem Mata Kesalahan (SMK) dikira semula setiap 3 tahun. Jika mencapai 20 mata, lesen anda akan digantung.'
          : ' The Traffic Demerit System (SMK) is reset every 3 years. If reaching 20 points, your license will be suspended.'}
      </p>
    </div>
  </div>
);

const VocationalDetailCard = ({ vl, language }) => {
  const detailFields = [
    { key: 'noLesen', label: language === 'bm' ? 'No. Lesen Vokasional' : 'Vocational License No.', value: vl.noLesen, mono: true, full: true },
    { key: 'issueDate', label: language === 'bm' ? 'Tarikh Dikeluarkan' : 'Issue Date', value: vl.issueDate },
    { key: 'expiryDate', label: language === 'bm' ? 'Tarikh Luput' : 'Expiry Date', value: vl.expiryDate },
    { key: 'syarikat', label: language === 'bm' ? 'Syarikat / Majikan' : 'Company / Employer', value: vl.syarikat },
    { key: 'jenisKenderaan', label: language === 'bm' ? 'Jenis Kenderaan' : 'Vehicle Type', value: vl.jenisKenderaan },
  ];
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: '#003580' }}>
        <div className="flex items-center gap-2">
          <span className="font-black text-white text-base px-2 py-0.5 rounded border border-yellow-400">{vl.jenis}</span>
          <span className="text-white font-semibold text-sm">{vl.namaLesen}</span>
        </div>
        <StatusBadge status={vl.status} />
      </div>
      <div className="p-4 bg-gray-50">
        <p className="text-xs text-gray-500 mb-3">{vl.keterangan}</p>
        <div className="grid grid-cols-2 gap-3">
          {detailFields.map((f) => (
            <div key={f.key} className={f.full ? 'col-span-2' : ''}>
              <div className="text-xs text-gray-400 mb-0.5">{f.label}</div>
              <div className={`text-sm font-semibold text-gray-800 ${f.mono ? 'font-mono tracking-wide' : ''}`}>{f.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const VocationalTab = ({ result, language }) => {
  const list = result.lesenVokasional || [];
  if (list.length === 0) {
    return (
      <div className="p-5">
        <div className="flex flex-col items-center py-10 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: '#f0f4ff' }}>
            <Truck size={28} style={{ color: '#003580' }} />
          </div>
          <h4 className="font-bold text-gray-700">{language === 'bm' ? 'Tiada Lesen Vokasional' : 'No Vocational License'}</h4>
          <p className="text-xs text-gray-400 mt-1 max-w-xs">
            {language === 'bm'
              ? 'Pemegang lesen ini tidak mempunyai sebarang lesen vokasional (GDL, PSV, CON).'
              : 'This license holder does not have any vocational licenses (GDL, PSV, CON).'}
          </p>
        </div>
      </div>
    );
  }

  const activeList = list.filter((vl) => vl.status === 'AKTIF');

  return (
    <div className="p-5">
      <div className="space-y-5">
        {activeList.map((vl) => (
          <VocationalLicenseCardMY key={vl.noLesen} result={result} vl={vl} />
        ))}

        <p className="text-xs text-gray-500 mt-1">
          {language === 'bm'
            ? 'Lesen vokasional membenarkan pemandu mengendalikan kenderaan komersial atau perkhidmatan awam.'
            : 'Vocational licenses authorize drivers to operate commercial or public service vehicles.'}
        </p>
        {list.map((vl) => (
          <VocationalDetailCard key={vl.noLesen} vl={vl} language={language} />
        ))}
      </div>
    </div>
  );
};

const ExamTab = ({ result, language }) => (
  <div className="p-5">
    <div className="mb-4 flex items-center gap-2">
      <Building2 size={16} style={{ color: '#003580' }} />
      <div>
        <div className="text-xs text-gray-400">{language === 'bm' ? 'Sekolah / Pusat Memandu' : 'Driving School / Centre'}</div>
        <div className="font-semibold text-sm text-gray-800">{result.sekolahMemandu}</div>
      </div>
    </div>
    <div className="border-t border-gray-100 pt-4">
      <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
        <Award size={15} style={{ color: '#003580' }} />
        {language === 'bm' ? 'Keputusan Ujian' : 'Test Results'}
      </h3>
      <div className="space-y-3">
        {result.ujianLulus.map((u, i) => (
          <div key={`${u.jenis}-${u.tarikh}`} className="border border-gray-100 rounded-lg p-3 flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-black text-xs"
              style={{ backgroundColor: u.keputusan === 'LULUS' ? '#16a34a' : '#cc0000' }}
            >
              {i + 1}
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-sm text-gray-800">{u.jenis}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                  u.keputusan === 'LULUS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {u.keputusan}
                </span>
              </div>
              <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                <span><span className="font-medium text-gray-600">{language === 'bm' ? 'Markah:' : 'Score:'}</span> {u.markah}</span>
                <span><span className="font-medium text-gray-600">{language === 'bm' ? 'Tarikh:' : 'Date:'}</span> {u.tarikh}</span>
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                <MapPin size={10} className="inline mr-1" />{u.tempatUjian}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TAB_RENDERERS = {
  overview: OverviewTab,
  classes: ClassesTab,
  smk: SmkTab,
  vocational: VocationalTab,
  exam: ExamTab,
};

export const LicenseTabs = ({ result, activeTab, setActiveTab, language }) => {
  const tabs = TAB_DEFS(language);
  const ActiveContent = TAB_RENDERERS[activeTab];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex border-b border-gray-100 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
              activeTab === tab.id
                ? 'border-blue-800 text-blue-800 bg-blue-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            style={activeTab === tab.id ? { borderColor: '#003580', color: '#003580' } : {}}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={15} />
            {tab.label}
            {tab.id === 'vocational' && result.lesenVokasional && result.lesenVokasional.length > 0 && (
              <span className="ml-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ fontSize: '10px' }}>
                {result.lesenVokasional.length}
              </span>
            )}
          </button>
        ))}
      </div>
      <ActiveContent result={result} language={language} />
    </div>
  );
};

export default LicenseTabs;
